import { useCallback } from "react";
import {
  BarChart2,
  Camera,
  ChartColumn,
  Clock,
  History,
  Redo2,
  Search,
  Settings,
  Share2,
  Trash2,
  TrendingUp,
  Undo2,
  Upload,
} from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { INDICATOR_CATALOG } from "@/lib/lytick/indicatorRegistry";
import { DRAWING_TOOLS } from "@/lib/lytick/drawingToolDefinitions";
import { DRAWING_TOOL_SHORTCUT_KEYS } from "@/lib/lytick/drawingToolDefinitions";
import { TIMEFRAMES } from "@/lib/lytick/chart-config";
import {
  canRedoDrawingAction,
  canUndoDrawingAction,
  clearAllDrawings,
  redoLastDrawingAction,
  undoLastDrawingAction,
} from "@/lib/lytick/overlay-bridge";
import { useChartStore } from "@/stores/chartStore";
import { useDrawingStore } from "@/stores/drawingStore";
import { useUIStore } from "@/stores/uiStore";
import { useDesktopPanelStore } from "@/stores/panelStore";
import { useReplayStore } from "@/stores/replayStore";
import { useSettingsStore } from "@/stores/settingsStore";
import { useChartSettings } from "@/hooks/useChartSettings";

const TIMEFRAME_LABELS: Record<string, string> = {
  "1m": "1 Min",
  "5m": "5 Min",
  "15m": "15 Min",
  "30m": "30 Min",
  "1h": "1 Hour",
  "2h": "2 Hour",
  "4h": "4 Hour",
  "8h": "8 Hour",
  "1d": "1 Day",
  "1w": "1 Week",
  "1Mn": "1 Month",
};

const TOP_INDICATORS = INDICATOR_CATALOG.slice(0, 15);

export function CommandPalette() {
  const commandPaletteOpen = useUIStore((s) => s.commandPaletteOpen);
  const closeCommandPalette = useUIStore((s) => s.closeCommandPalette);

  const setTimeframe = useChartStore((s) => s.setTimeframe);
  const setActiveTool = useDrawingStore((s) => s.setActiveTool);
  const { addIndicatorFromCatalog } = useChartSettings();

  const toggleIndicators = useDesktopPanelStore((s) => s.toggleIndicators);
  const toggleSettings = useUIStore((s) => s.toggleSettings);
  const enterReplayMode = useReplayStore((s) => s.enterReplayMode);
  const exitReplayMode = useReplayStore((s) => s.exitReplayMode);
  const isReplayMode = useReplayStore((s) => s.isReplayMode);

  const recentIndicatorNames = useSettingsStore((s) => s.recentIndicators);
  const recordRecentIndicator = useSettingsStore((s) => s.recordRecentIndicator);

  const recentEntries = recentIndicatorNames
    .map((name) => INDICATOR_CATALOG.find((e) => e.name === name))
    .filter(Boolean) as typeof INDICATOR_CATALOG;

  const run = useCallback((fn: () => void) => {
    closeCommandPalette();
    fn();
  }, [closeCommandPalette]);

  return (
    <CommandDialog
      open={commandPaletteOpen}
      onOpenChange={(open) => { if (!open) closeCommandPalette(); }}
      title="Command Palette"
      description="Search for any action, tool, indicator, or timeframe"
      className="max-w-[520px]"
      showCloseButton={false}
    >
      <CommandInput placeholder="Search commands..." />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Drawing Tools */}
        <CommandGroup heading="Drawing Tools">
          {DRAWING_TOOLS.map((tool) => (
            <CommandItem
              key={tool.id}
              value={`drawing ${tool.label} ${tool.id}`}
              onSelect={() => run(() => setActiveTool(tool.id as any))}
            >
              <TrendingUp className="h-4 w-4" />
              <span>{tool.label}</span>
              {DRAWING_TOOL_SHORTCUT_KEYS[tool.id] && (
                <CommandShortcut>{DRAWING_TOOL_SHORTCUT_KEYS[tool.id]}</CommandShortcut>
              )}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {/* Timeframes */}
        <CommandGroup heading="Timeframe">
          {TIMEFRAMES.map((tf) => (
            <CommandItem
              key={tf}
              value={`timeframe ${tf} ${TIMEFRAME_LABELS[tf] ?? tf}`}
              onSelect={() => run(() => setTimeframe(tf as any))}
            >
              <Clock className="h-4 w-4" />
              <span>{TIMEFRAME_LABELS[tf] ?? tf}</span>
              <CommandShortcut>{tf.toUpperCase()}</CommandShortcut>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {/* Recently used indicators */}
        {recentEntries.length > 0 && (
          <>
            <CommandGroup heading="Recent">
              {recentEntries.map((entry) => (
                <CommandItem
                  key={`recent-${entry.name}`}
                  value={`recent indicator ${entry.displayName} ${entry.shortName}`}
                  onSelect={() => run(() => { recordRecentIndicator(entry.name); addIndicatorFromCatalog(entry); })}
                >
                  <BarChart2 className="h-4 w-4" />
                  <span>{entry.displayName}</span>
                  <CommandShortcut>{entry.shortName}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Indicators */}
        <CommandGroup heading="Indicators">
          {TOP_INDICATORS.map((entry) => (
            <CommandItem
              key={entry.name}
              value={`indicator ${entry.displayName} ${entry.shortName} ${entry.description}`}
              onSelect={() => run(() => { recordRecentIndicator(entry.name); addIndicatorFromCatalog(entry); })}
            >
              <BarChart2 className="h-4 w-4" />
              <span>{entry.displayName}</span>
              <CommandShortcut>{entry.shortName}</CommandShortcut>
            </CommandItem>
          ))}
          <CommandItem
            value="indicator browse all open panel"
            onSelect={() => run(toggleIndicators)}
          >
            <ChartColumn className="h-4 w-4" />
            <span>Browse all indicators…</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* Drawing Actions */}
        <CommandGroup heading="Drawings">
          <CommandItem
            value="undo drawing action"
            disabled={!canUndoDrawingAction()}
            onSelect={() => run(undoLastDrawingAction)}
          >
            <Undo2 className="h-4 w-4" />
            <span>Undo</span>
            <CommandShortcut>Ctrl+Z</CommandShortcut>
          </CommandItem>
          <CommandItem
            value="redo drawing action"
            disabled={!canRedoDrawingAction()}
            onSelect={() => run(redoLastDrawingAction)}
          >
            <Redo2 className="h-4 w-4" />
            <span>Redo</span>
            <CommandShortcut>Ctrl+Y</CommandShortcut>
          </CommandItem>
          <CommandItem
            value="clear all drawings remove delete"
            onSelect={() => run(clearAllDrawings)}
          >
            <Trash2 className="h-4 w-4" />
            <span>Clear All Drawings</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* App Actions */}
        <CommandGroup heading="App">
          <CommandItem
            value="search symbol market open"
            onSelect={() => run(() => window.dispatchEvent(new CustomEvent("lytick:open-market-search", { detail: { key: "" } })))}
          >
            <Search className="h-4 w-4" />
            <span>Search Symbol…</span>
          </CommandItem>
          <CommandItem
            value={isReplayMode ? "exit bar replay mode" : "enter bar replay mode start"}
            onSelect={() => run(isReplayMode ? exitReplayMode : enterReplayMode)}
          >
            <History className="h-4 w-4" />
            <span>{isReplayMode ? "Exit Replay" : "Enter Replay"}</span>
          </CommandItem>
          <CommandItem
            value="load csv file import mt5 data offline"
            onSelect={() => run(() => window.dispatchEvent(new CustomEvent("lytick:load-csv")))}
          >
            <Upload className="h-4 w-4" />
            <span>Load CSV file…</span>
          </CommandItem>
          <CommandItem
            value="snapshot save chart png image"
            onSelect={() => run(() => window.dispatchEvent(new CustomEvent("lytick:snapshot")))}
          >
            <Camera className="h-4 w-4" />
            <span>Snapshot — Save as PNG</span>
          </CommandItem>
          <CommandItem
            value="share copy link url chart"
            onSelect={() => run(() => window.dispatchEvent(new CustomEvent("lytick:share")))}
          >
            <Share2 className="h-4 w-4" />
            <span>Copy Chart Link</span>
          </CommandItem>
          <CommandItem
            value="open settings preferences"
            onSelect={() => run(toggleSettings)}
          >
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
